import { AlertTriangle, RefreshCw } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error: error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console and error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        // Clone the fallback element and inject error props
        return React.cloneElement(this.props.fallback, {
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          resetError: this.handleReset,
        });
      }

      // Default error UI
      return (
        <div
          className='min-h-screen bg-background flex items-center justify-center p-4'
          data-testid='error-fallback'
        >
          <Card className='w-full max-w-md'>
            <CardHeader className='text-center'>
              <div className='mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10'>
                <AlertTriangle className='h-6 w-6 text-destructive' />
              </div>
              <CardTitle className='text-xl'>Something went wrong</CardTitle>
              <CardDescription>
                An unexpected error occurred while loading the system settings.
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-4'>
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className='rounded-md bg-muted p-3'>
                  <h4 className='text-sm font-medium mb-2'>Error Details:</h4>
                  <pre className='text-xs text-muted-foreground whitespace-pre-wrap'>
                    {this.state.error.toString()}
                  </pre>
                  {this.state.errorInfo && (
                    <details className='mt-2'>
                      <summary className='text-xs cursor-pointer'>
                        Stack Trace
                      </summary>
                      <pre className='text-xs text-muted-foreground whitespace-pre-wrap mt-1'>
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              <div className='flex flex-col space-y-2'>
                <Button onClick={this.handleReset} className='w-full'>
                  <RefreshCw className='mr-2 h-4 w-4' />
                  Try Again
                </Button>

                <Button
                  variant='outline'
                  onClick={() => window.location.reload()}
                  className='w-full'
                >
                  Reload Page
                </Button>
              </div>

              <p className='text-xs text-muted-foreground text-center'>
                If the problem persists, please contact support.
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
